<script lang="ts">
  import { Button, ButtonLink } from '@athena-lib/Button';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { TextField } from '@athena-lib/Fields';
  import { HeadingSmall, Text } from '@athena-lib/Text';
  import type { LoginFormHandler } from '@athena-lib/LoginForm';
  import { onMount } from 'svelte';
  import { handler, required } from './utils';
  import Notications from './Notifications.svelte';
  import { type LoginFormTrackingHandler } from './types';

  type Props = {
    googleLoginHref: string;
    onLogin: LoginFormHandler;
    goToResetPassword: () => void;
    goToCreateAccount: () => void;
    onFormTracking: LoginFormTrackingHandler;
  };

  let { googleLoginHref, onLogin, goToResetPassword, goToCreateAccount, onFormTracking }: Props =
    $props();

  let email = $state('');
  let password = $state('');
  let errors = $state<string[] | undefined>(undefined);
  let messages = $state<string[] | undefined>(undefined);

  let loginFormElement: HTMLFormElement | undefined = undefined;

  const canSubmit = $derived(email && password);

  const translations = getCommonTranslations();

  const handleLogin = handler(onLogin, {
    defaultMessage: translations.generalError,
    onSuccess: (n) => {
      onFormTracking('submit', 'login', true);
      messages = n;
    },
    onError: (e) => {
      onFormTracking('error', 'login', true);
      errors = e;
    },
    reset: () => {
      errors = undefined;
      messages = undefined;
    },
  });

  onMount(() => {
    onFormTracking('show', 'login', false);
  });

  const maybeTriggerCompleteEvent = () => {
    if (!loginFormElement?.checkValidity()) {
      return;
    }

    onFormTracking('complete', 'login', false);
  };
</script>

<div class="flex flex-col">
  <div class="text-center">
    <HeadingSmall size="small">{translations.checkoutLoginFormTitle}</HeadingSmall>
    <Text element="span">{translations.checkoutLoginFormDescription}</Text>
  </div>
  {#if errors || messages}
    <Notications {errors} {messages} />
  {/if}
  <form
    bind:this={loginFormElement}
    onsubmit={handleLogin}
    class="mt-8 flex flex-col gap-4 data-[has-notifications=true]:mt-0"
    data-has-notifications={!!errors || !!messages}
  >
    <TextField
      type="email"
      name="email"
      label={required(translations.email)}
      bind:value={email}
      required
      oninput={() => {
        onFormTracking('start', 'login', false);
        maybeTriggerCompleteEvent();
      }}
    />
    <TextField
      type="password"
      name="password"
      label={required(translations.password)}
      bind:value={password}
      required
      oninput={() => {
        onFormTracking('start', 'login', false);
        maybeTriggerCompleteEvent();
      }}
    />
    <div class="flex justify-center">
      <Button type="submit" class="max-w-fit" disabled={!canSubmit} wide>
        {translations.signIn}
      </Button>
    </div>
  </form>
  <div class="mt-4 flex flex-col items-center gap-4">
    <button type="button" onclick={goToResetPassword}>
      <Text element="span" class="underline">{translations.forgotPassword}</Text>
    </button>
    <Text>
      {translations.dontHaveAnAccount}
      <button type="button" onclick={goToCreateAccount}>
        <Text element="span" class="underline">{translations.createHere}</Text>
      </button>
    </Text>
  </div>
  <div class="my-6 flex items-center">
    <div class="h-[1px] w-full bg-st-gray-600"></div>
    <Text class="mx-2 uppercase">{translations.or}</Text>
    <div class="h-[1px] w-full bg-st-gray-600"></div>
  </div>
  <div data-sveltekit-reload={true}>
    <ButtonLink
      link={{
        href: googleLoginHref + `?currentPath=${encodeURIComponent(window.location.pathname)}`,
      }}
      wide
      size="small"
      class="flex w-full flex-row items-center gap-0 !rounded-full border-2 border-solid border-[#4285f4] bg-white pl-2 hover:border-[#3367d6] hover:!bg-[#3367d6] hover:text-st-white"
    >
      <div class="rounded-full bg-white p-2">
        <img src="https://d6ce0no7ktiq.cloudfront.net/images/svg/google_g.svg" alt="google" />
      </div>
      <div class="flex-grow">
        {translations.signInWithGoogle}
      </div>
    </ButtonLink>
  </div>
</div>
